import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    CheckCircleOutline,
    ContentCopy,
    LockOutlined,
    PublicOutlined,
    Replay,
    RadioButtonChecked,
    RadioButtonUnchecked,
    InfoOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAtomValue } from 'jotai';
import { chatbotConfigState } from './state/config/ChatbotConfigState';
import { useAppTranslation } from './hooks/useAppTranslation';

import { AdminPageHeader } from './components/admin/AdminPageHeader';
import { ChatbotCustomizationPanel } from './components/admin/ChatbotCustomizationPanel';
import { PromptUpdateField } from './components/admin/PromptUpdateField';
import { DrawioUploadPanel } from './components/admin/DrawioUploadPanel';
import { ZipInfoDialogContent } from './components/admin/ZipInfoDialogContent';
import { KnowledgeBasePanel } from './components/admin/KnowledgeBasePanel';
import { getTenantChatbots, createChatbot, updateChatbot } from './utils/server/storageUtils';
import { getActiveChatbotId, getActiveTenantId, setActiveChatbotId, setActiveTenantId } from './utils/server/authUtils';

function formatDomainsForField(domains = []) {
    return Array.isArray(domains) ? domains.join('\n') : '';
}

function parseDomainsFromField(value = '') {
    return [...new Set(
        String(value || '')
            .split(/[\n,]/)
            .map((item) => item.trim())
            .filter(Boolean)
    )];
}

function isValidUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim());
}

export default function AdminInterface() {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const config = useAtomValue(chatbotConfigState);
    const { t } = useAppTranslation();

    const initialTenantId = getActiveTenantId() || null;
    const [chatbots, setChatbots] = useState([]);
    const [activeBotId, setActiveBotIdState] = useState(() => {
        const storedBotId = getActiveChatbotId({ tenantId: initialTenantId }) || '';
        return isValidUuid(storedBotId) ? storedBotId : '';
    });
    const [isLoadingBots, setIsLoadingBots] = useState(false);
    const [tenantId, setTenantId] = useState(initialTenantId);
    const [availableTenants, setAvailableTenants] = useState([]);
    const [botError, setBotError] = useState('');
    const [botSuccess, setBotSuccess] = useState('');
    const [isSavingBotSettings, setIsSavingBotSettings] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newBotName, setNewBotName] = useState('');
    const [isCreatingBot, setIsCreatingBot] = useState(false);
    const [createDialogError, setCreateDialogError] = useState('');
    const [copiedField, setCopiedField] = useState('');

    const [botNameInput, setBotNameInput] = useState('');
    const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
    const [isPublished, setIsPublished] = useState(false);
    const [isActive, setIsActive] = useState(true);

    const primaryColor = config.primaryColor || '#172983';
    const secondaryColor = config.secondaryColor || '#068945';

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [navigate, user]);

    useEffect(() => {
        if (!botSuccess) return undefined;

        const timer = window.setTimeout(() => setBotSuccess(''), 4200);
        return () => window.clearTimeout(timer);
    }, [botSuccess]);

    useEffect(() => {
        if (!copiedField) return undefined;

        const timer = window.setTimeout(() => setCopiedField(''), 1800);
        return () => window.clearTimeout(timer);
    }, [copiedField]);

    const loadChatbots = useCallback(async (preferredBotId = null, tenantOverride = null) => {
        if (!token) return;

        setIsLoadingBots(true);
        setBotError('');

        try {
            const requestedTenantId = tenantOverride || getActiveTenantId({ token }) || null;
            const response = await getTenantChatbots(token, requestedTenantId);

            const resolvedTenantId = response?.tenantId || requestedTenantId || null;
            let loadedChatbots = Array.isArray(response?.chatbots) ? response.chatbots.filter((bot) => isValidUuid(bot?.id)) : [];

            if (resolvedTenantId && loadedChatbots.length === 0) {
                try {
                    const createResponse = await createChatbot(token, { name: 'Default Chatbot' }, resolvedTenantId);
                    const createdBot = createResponse?.chatbot;
                    if (createdBot?.id && isValidUuid(createdBot.id)) {
                        loadedChatbots = [createdBot];
                    }
                } catch (createError) {
                    console.error('Auto-create chatbot failed', createError);
                }
            }

            setChatbots(loadedChatbots);
            setTenantId(resolvedTenantId);
            setAvailableTenants(response?.availableTenants || []);

            if (resolvedTenantId) {
                setActiveTenantId(resolvedTenantId, { token });
            }

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('active-tenant-changed', {
                    detail: { tenantId: resolvedTenantId },
                }));
            }

            const rawStoredBotId = preferredBotId || getActiveChatbotId({ token, tenantId: resolvedTenantId });
            const storedBotId = isValidUuid(rawStoredBotId) ? rawStoredBotId : '';
            const resolvedBotId =
                storedBotId && loadedChatbots.some((bot) => bot.id === storedBotId)
                    ? storedBotId
                    : loadedChatbots[0]?.id || '';

            if (resolvedBotId && isValidUuid(resolvedBotId)) {
                setActiveChatbotId(resolvedBotId, { token, tenantId: resolvedTenantId });
                setActiveBotIdState(resolvedBotId);

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('active-chatbot-changed', {
                        detail: { chatbotId: resolvedBotId, tenantId: resolvedTenantId },
                    }));
                }
            } else {
                setActiveChatbotId(null, { token, tenantId: resolvedTenantId });
                setActiveBotIdState('');
            }
        } catch (error) {
            console.error('Failed to load tenant chatbots', error);
            setBotError(error?.response?.data?.error || t.adminErrorLoadTenantChatbots);
        } finally {
            setIsLoadingBots(false);
        }
    }, [token, t]);

    useEffect(() => {
        loadChatbots();
    }, [loadChatbots]);

    const activeChatbot = useMemo(
        () => chatbots.find((bot) => bot.id === activeBotId) || null,
        [activeBotId, chatbots]
    );

    useEffect(() => {
        setBotNameInput(activeChatbot?.name || '');
        setAllowedDomainsInput(formatDomainsForField(activeChatbot?.allowedDomains || []));
        setIsPublished(Boolean(activeChatbot?.isPublished));
        setIsActive(activeChatbot?.isActive !== false);
    }, [activeChatbot]);

    const normalizedPersistedDomains = useMemo(
        () => parseDomainsFromField(formatDomainsForField(activeChatbot?.allowedDomains || [])).sort(),
        [activeChatbot]
    );
    const normalizedEditedDomains = useMemo(
        () => parseDomainsFromField(allowedDomainsInput).sort(),
        [allowedDomainsInput]
    );
    const trimmedEditedName = useMemo(() => String(botNameInput || '').trim(), [botNameInput]);
    const duplicateEditedBotName = useMemo(() => {
        if (!activeChatbot || !trimmedEditedName) return false;

        return chatbots.some((bot) => (
            bot.id !== activeChatbot.id
            && String(bot.name || '').trim().toLowerCase() === trimmedEditedName.toLowerCase()
        ));
    }, [activeChatbot, chatbots, trimmedEditedName]);
    const duplicateCreateBotName = useMemo(() => {
        const normalizedName = String(newBotName || '').trim().toLowerCase();
        if (!normalizedName) return false;

        return chatbots.some((bot) => String(bot.name || '').trim().toLowerCase() === normalizedName);
    }, [chatbots, newBotName]);

    const hasBotSettingsChanges = useMemo(() => {
        if (!activeChatbot) return false;

        const persistedName = String(activeChatbot.name || '').trim();
        const persistedIsActive = activeChatbot.isActive !== false;

        return (
            trimmedEditedName !== persistedName ||
            Boolean(isPublished) !== Boolean(activeChatbot.isPublished) ||
            Boolean(isActive) !== Boolean(persistedIsActive) ||
            normalizedEditedDomains.length !== normalizedPersistedDomains.length ||
            normalizedEditedDomains.some((domain, index) => domain !== normalizedPersistedDomains[index])
        );
    }, [
        activeChatbot,
        trimmedEditedName,
        isPublished,
        isActive,
        normalizedEditedDomains,
        normalizedPersistedDomains,
    ]);

    const copyToClipboard = useCallback(async (value, fieldKey) => {
        const text = String(value || '').trim();
        if (!text) return;

        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const tempTextArea = document.createElement('textarea');
                tempTextArea.value = text;
                tempTextArea.setAttribute('readonly', '');
                tempTextArea.style.position = 'absolute';
                tempTextArea.style.left = '-9999px';
                document.body.appendChild(tempTextArea);
                tempTextArea.select();
                document.execCommand('copy');
                document.body.removeChild(tempTextArea);
            }

            setCopiedField(fieldKey);
            const copyLabels = {
                embed: t.adminCopiedEmbed,
                iframe: t.adminCopiedIframe,
                'direct-link': t.adminCopiedDirectLink,
            };
            setBotSuccess(copyLabels[fieldKey] || t.adminCopiedGeneric);
        } catch (error) {
            console.error('Copy failed', error);
            setBotError(t.adminErrorCopyClipboard);
        }
    }, [t]);

    const handleChangeChatbot = (event) => {
        const nextBotId = event.target.value;

        setBotError('');
        setBotSuccess('');

        const sanitizedBotId = isValidUuid(nextBotId) ? nextBotId : '';

        setActiveBotIdState(sanitizedBotId);
        setActiveChatbotId(sanitizedBotId || null, { token, tenantId });

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('active-chatbot-changed', {
                detail: { chatbotId: sanitizedBotId || null, tenantId },
            }));
        }
    };

    const handleChangeTenant = async (event) => {
        const nextTenantId = event.target.value;

        setBotError('');
        setBotSuccess('');
        setTenantId(nextTenantId);
        setActiveBotIdState('');
        setChatbots([]);
        setActiveTenantId(nextTenantId, { token });

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('active-tenant-changed', {
                detail: { tenantId: nextTenantId },
            }));
        }

        await loadChatbots(null, nextTenantId);
    };

    const handleOpenCreateChatbotDialog = () => {
        setBotError('');
        setBotSuccess('');
        setCreateDialogError('');
        setNewBotName('');
        setIsCreateDialogOpen(true);
    };

    const handleCloseCreateChatbotDialog = () => {
        if (isCreatingBot) return;

        setIsCreateDialogOpen(false);
        setNewBotName('');
        setCreateDialogError('');
    };

    const handleCreateChatbot = async () => {
        if (!token || isCreatingBot) return;

        const name = String(newBotName || '').trim();
        if (!name) {
            setCreateDialogError(t.adminErrorEnterName);
            return;
        }

        if (duplicateCreateBotName) {
            setCreateDialogError(t.adminErrorNameExistsInTenant);
            return;
        }

        setIsCreatingBot(true);
        setCreateDialogError('');
        setBotError('');
        setBotSuccess('');

        try {
            const response = await createChatbot(token, { name }, tenantId);
            const newBot = response?.chatbot;

            if (!newBot?.id) {
                throw new Error(t.adminErrorNoChatbotReturnedCreate);
            }

            await loadChatbots(newBot.id, tenantId);

            setIsCreateDialogOpen(false);
            setNewBotName('');
            setCreateDialogError('');
            setBotSuccess(t.adminSuccessCreated);

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('active-chatbot-changed', {
                    detail: { chatbotId: newBot.id, tenantId },
                }));
            }
        } catch (error) {
            console.error('Failed to create chatbot', error);
            const message = error?.response?.data?.error || t.adminErrorCreateFailed;
            setCreateDialogError(message);
            setBotError(message);
        } finally {
            setIsCreatingBot(false);
        }
    };

    const handleResetBotSettings = () => {
        if (!activeChatbot) return;

        setBotNameInput(activeChatbot?.name || '');
        setAllowedDomainsInput(formatDomainsForField(activeChatbot?.allowedDomains || []));
        setIsPublished(Boolean(activeChatbot?.isPublished));
        setIsActive(activeChatbot?.isActive !== false);
        setBotError('');
        setBotSuccess(t.adminSuccessReset);
    };

    const handleSaveBotSettings = async () => {
        if (!token || !activeChatbot?.id) return;

        const normalizedDomains = normalizedEditedDomains;

        if (!trimmedEditedName) {
            setBotError(t.adminErrorNameEmpty);
            return;
        }

        if (duplicateEditedBotName) {
            setBotError(t.adminErrorNameDuplicate);
            return;
        }

        if (isPublished && normalizedDomains.length === 0) {
            setBotError(t.adminErrorNeedDomainToPublish);
            return;
        }

        setIsSavingBotSettings(true);
        setBotError('');
        setBotSuccess('');

        try {
            const response = await updateChatbot(token, activeChatbot.id, {
                name: trimmedEditedName,
                allowedDomains: normalizedDomains,
                isPublished,
                isActive,
            }, tenantId);

            const updatedBot = response?.chatbot;
            if (!updatedBot) {
                throw new Error(t.adminErrorNoChatbotReturnedUpdate);
            }

            setChatbots((current) =>
                current.map((bot) => (bot.id === updatedBot.id ? updatedBot : bot))
            );

            setBotSuccess(t.adminSuccessBotSettingsSaved);
        } catch (error) {
            console.error('Failed to update chatbot settings', error);
            setBotError(error?.response?.data?.error || t.adminErrorBotSettingsSaveFailed);
        } finally {
            setIsSavingBotSettings(false);
        }
    };

    if (!user) return null;

    const activePanelKey = `${tenantId || 'no-tenant'}:${activeBotId || 'no-bot-selected'}`;
    const tenantOptions = (
        availableTenants.length > 0
            ? availableTenants
            : tenantId
                ? [{ tenantId, role: 'owner' }]
                : []
    ).filter((tenant) => tenant?.tenantId);
    const safeTenantSelectValue = tenantOptions.some((tenant) => tenant.tenantId === tenantId)
        ? (tenantId || '')
        : '';
    const safeActiveBotSelectValue = chatbots.some((bot) => bot.id === activeBotId)
        ? activeBotId
        : '';

    const hasReachedBotLimit = chatbots.length >= 1;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
                height: '100%',
                flex: '1 1 auto',
                boxSizing: 'border-box',
                position: 'relative',
                overflowX: 'hidden',
                background: 'var(--hero-glow), var(--admin-page-bg)',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        borderRadius: '50%',
                        filter: 'blur(88px)',
                        opacity: 0.24,
                    },
                    '&::before': {
                        width: 300,
                        height: 300,
                        top: 48,
                        left: 32,
                        backgroundColor: 'var(--admin-page-glow-1)',
                    },
                    '&::after': {
                        width: 320,
                        height: 320,
                        right: 32,
                        bottom: 48,
                        backgroundColor: 'var(--admin-page-glow-2)',
                    },
                }}
            />

            <Container
                maxWidth={false}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    py: { xs: 2, md: 4 },
                    pb: { xs: 14, md: 18 },
                    px: { xs: 1.5, sm: 2.5 },
                    maxWidth: '1680px',
                    mx: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <AdminPageHeader />

                    <Dialog
                        open={isCreateDialogOpen}
                        onClose={handleCloseCreateChatbotDialog}
                        fullWidth
                        maxWidth='xs'
                        PaperProps={{
                            sx: {
                                borderRadius: '24px',
                                border: '1px solid var(--border-color)',
                                boxShadow: 'var(--elevated-shadow)',
                            },
                        }}
                    >
                        <DialogTitle sx={{ fontWeight: 800 }}>{t.adminCreateNewChatbotTitle}</DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <Typography sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.92rem' }}>
                                {t.adminCreateNewChatbotBody}
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                label={t.adminCreateNewChatbotNameLabel}
                                value={newBotName}
                                onChange={(event) => {
                                    setNewBotName(event.target.value);
                                    if (createDialogError) setCreateDialogError('');
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleCreateChatbot();
                                    }
                                }}
                                placeholder={t.adminCreateNewChatbotPlaceholder}
                                helperText={
                                    createDialogError
                                        || (duplicateCreateBotName
                                            ? t.adminCreateNewChatbotHelperDuplicate
                                            : t.adminCreateNewChatbotHelperDefault)
                                }
                                error={Boolean(createDialogError || duplicateCreateBotName)}
                                inputProps={{ maxLength: 80 }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5 }}>
                            <Button
                                onClick={handleCloseCreateChatbotDialog}
                                disabled={isCreatingBot}
                                sx={{ textTransform: 'none', borderRadius: '12px' }}
                            >
                                {t.cancel}
                            </Button>
                            <Button
                                variant='contained'
                                onClick={handleCreateChatbot}
                                disabled={!token || isCreatingBot || !newBotName.trim() || duplicateCreateBotName}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    fontWeight: 700,
                                    minWidth: 148,
                                }}
                            >
                                {isCreatingBot ? t.adminCreatingChatbot : t.adminCreateChatbot}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Box
                        sx={{
                            p: 2.25,
                            borderRadius: '28px',
                            border: '1px solid var(--admin-section-border)',
                            background: 'var(--admin-section-bg)',
                            boxShadow: 'var(--card-shadow)',
                            backdropFilter: 'blur(10px)',
                            '& .MuiInputLabel-root': {
                                color: 'var(--admin-section-subtle)',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: 'var(--accent-color)',
                            },
                            '& .MuiOutlinedInput-root': {
                                color: 'var(--admin-section-title)',
                                backgroundColor: 'var(--surface-fill-strong)',
                                '& fieldset': {
                                    borderColor: 'var(--border-color)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'var(--text-muted)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'var(--accent-color)',
                                },
                            },
                            '& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-inputMultiline.Mui-disabled': {
                                WebkitTextFillColor: 'var(--admin-section-title)',
                            },
                            '& .MuiFormHelperText-root': {
                                color: 'var(--admin-section-subtle)',
                            },
                            '& .MuiSelect-icon': {
                                color: 'var(--admin-section-subtle)',
                            },
                            '& .MuiSwitch-root .MuiSwitch-switchBase.Mui-checked': {
                                color: 'var(--accent-color)',
                            },
                            '& .MuiSwitch-root .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'var(--accent-color)',
                            },
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                justifyContent='space-between'
                                alignItems={{ xs: 'stretch', md: 'center' }}
                            >
                                <Box>
                                    <Typography variant='h6' sx={{ color: 'var(--admin-section-title)', fontWeight: 700 }}>
                                        {t.adminActiveChatbotSectionTitle}
                                    </Typography>
                                    <Typography variant='body2' sx={{ color: 'var(--admin-section-text)' }}>
                                        {t.adminTenantLabel}: {tenantId || t.loading}
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            size='small'
                                            icon={isActive ? <RadioButtonChecked /> : <RadioButtonUnchecked />}
                                            label={isActive ? t.adminBotActive : t.adminBotPaused}
                                            sx={{
                                                color: 'var(--admin-section-chip-text)',
                                                backgroundColor: 'var(--admin-section-chip-bg)',
                                                border: '1px solid var(--admin-section-chip-border)',
                                            }}
                                        />
                                        <Chip
                                            size='small'
                                            icon={isPublished ? <PublicOutlined /> : <LockOutlined />}
                                            label={isPublished ? t.adminPublicWidgetLive : t.adminPrivateDraft}
                                            sx={{
                                                color: 'var(--admin-section-chip-text)',
                                                backgroundColor: 'var(--admin-section-chip-bg)',
                                                border: '1px solid var(--admin-section-chip-border)',
                                            }}
                                        />
                                        <Chip
                                            size='small'
                                            icon={<CheckCircleOutline />}
                                            label={`${normalizedEditedDomains.length} ${normalizedEditedDomains.length === 1 ? t.adminAllowedDomainSingular : t.adminAllowedDomainPlural}`}
                                            sx={{
                                                color: 'var(--admin-section-chip-text)',
                                                backgroundColor: 'var(--admin-section-chip-bg)',
                                                border: '1px solid var(--admin-section-chip-border)',
                                            }}
                                        />
                                    </Stack>
                                    {activeChatbot?.publicBotId && (
                                        <Typography variant='caption' sx={{ color: 'var(--admin-section-subtle)', display: 'block', mt: 1 }}>
                                            {t.adminPublicBotId}: {activeChatbot.publicBotId}
                                        </Typography>
                                    )}
                                </Box>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <FormControl size='small' sx={{ minWidth: 220 }}>
                                        <InputLabel id='active-tenant-label'>{t.adminTenantLabel}</InputLabel>
                                        <Select
                                            labelId='active-tenant-label'
                                            value={safeTenantSelectValue}
                                            label={t.adminTenantLabel}
                                            onChange={handleChangeTenant}
                                            disabled={isLoadingBots || availableTenants.length <= 1}
                                        >
                                            {tenantOptions.length === 0 && (
                                                <MenuItem value='' disabled>
                                                    {t.adminNoTenantAvailable}
                                                </MenuItem>
                                            )}
                                            {tenantOptions.map((tenant) => (
                                                <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                                                    {tenant.tenantId}{tenant.role ? ` (${tenant.role})` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl size='small' sx={{ minWidth: 260 }}>
                                        <InputLabel id='active-chatbot-label'>{t.adminChatbotLabel}</InputLabel>
                                        <Select
                                            labelId='active-chatbot-label'
                                            value={safeActiveBotSelectValue}
                                            label={t.adminChatbotLabel}
                                            onChange={handleChangeChatbot}
                                            disabled={isLoadingBots || chatbots.length === 0}
                                        >
                                            {chatbots.length === 0 && (
                                                <MenuItem value='' disabled>
                                                    {t.adminNoChatbotsAvailable}
                                                </MenuItem>
                                            )}
                                            {chatbots.map((bot) => (
                                                <MenuItem key={bot.id} value={bot.id}>
                                                    {bot.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Tooltip title={hasReachedBotLimit ? t.adminPlanLimitReached : ''}>
                                        <span>
                                            <Button
                                                variant='contained'
                                                onClick={handleOpenCreateChatbotDialog}
                                                disabled={!token || isCreatingBot || hasReachedBotLimit}
                                                sx={{
                                                    minWidth: 160,
                                                    borderRadius: '14px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    '&.Mui-disabled': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                                        color: 'rgba(255, 255, 255, 0.3)',
                                                    }
                                                }}
                                            >
                                                {isCreatingBot ? t.adminCreatingChatbot : t.adminNewChatbotButton}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>

                            <Divider sx={{ borderColor: 'var(--border-color)' }} />

                            {!activeChatbot && (
                                <Alert severity='info' sx={{ borderRadius: '16px' }}>
                                    {t.adminNoActiveChatbotAlert}
                                </Alert>
                            )}

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    label={t.adminCreateNewChatbotNameLabel}
                                    value={botNameInput}
                                    onChange={(event) => setBotNameInput(event.target.value)}
                                    fullWidth
                                    size='small'
                                    disabled={!activeChatbot}
                                    error={duplicateEditedBotName || (!!activeChatbot && !trimmedEditedName)}
                                    helperText={
                                        !activeChatbot
                                            ? t.adminSelectActiveChatbotFirst
                                            : duplicateEditedBotName
                                                ? t.adminErrorNameDuplicate
                                                : !trimmedEditedName
                                                    ? t.adminErrorNameEmpty
                                                    : t.adminChatbotNameHelperInternal
                                    }
                                    inputProps={{ maxLength: 80 }}
                                />
                                <TextField
                                    label={t.adminAllowedDomainsLabel}
                                    value={allowedDomainsInput}
                                    onChange={(event) => setAllowedDomainsInput(event.target.value)}
                                    fullWidth
                                    size='small'
                                    disabled={!activeChatbot}
                                    multiline
                                    minRows={3}
                                    placeholder={'example.com\napp.example.com'}
                                    helperText={
                                        !activeChatbot
                                            ? t.adminSelectActiveChatbotFirst
                                            : `${normalizedEditedDomains.length} ${normalizedEditedDomains.length === 1 ? t.adminAllowedDomainSingular : t.adminAllowedDomainPlural}`
                                    }
                                />
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                justifyContent='space-between'
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <FormControlLabel
                                        control={(
                                            <Switch
                                                checked={isActive}
                                                onChange={(event) => setIsActive(event.target.checked)}
                                                disabled={!activeChatbot}
                                            />
                                        )}
                                        label={t.adminBotActive}
                                        sx={{ color: 'var(--admin-section-title)' }}
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Switch
                                                checked={isPublished}
                                                onChange={(event) => setIsPublished(event.target.checked)}
                                                disabled={!activeChatbot}
                                            />
                                        )}
                                        label={t.adminPublicWidgetPublished}
                                        sx={{ color: 'var(--admin-section-title)' }}
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                    <Button
                                        variant='outlined'
                                        onClick={handleResetBotSettings}
                                        disabled={!activeChatbot?.id || isSavingBotSettings || !hasBotSettingsChanges}
                                        startIcon={<Replay />}
                                        sx={{
                                            minWidth: 140,
                                            borderRadius: '14px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderColor: 'var(--admin-ghost-button-border)',
                                            color: 'var(--admin-ghost-button-text)',
                                            '&:hover': {
                                                borderColor: 'var(--admin-ghost-button-border)',
                                                backgroundColor: 'var(--admin-ghost-button-hover)',
                                            },
                                            '&.Mui-disabled': {
                                                color: 'var(--admin-section-subtle)',
                                                borderColor: 'var(--border-color)',
                                            },
                                        }}
                                    >
                                        {t.reset}
                                    </Button>
                                    <Button
                                        variant='contained'
                                        onClick={handleSaveBotSettings}
                                        disabled={!activeChatbot?.id || isSavingBotSettings || !hasBotSettingsChanges || duplicateEditedBotName || !trimmedEditedName}
                                        sx={{
                                            minWidth: 190,
                                            borderRadius: '14px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            background: hasBotSettingsChanges
                                                ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
                                                : 'rgba(148, 163, 184, 0.28)',
                                            boxShadow: hasBotSettingsChanges
                                                ? '0 18px 30px rgba(15, 23, 42, 0.22)'
                                                : 'none',
                                            '&.Mui-disabled': {
                                                color: 'var(--admin-section-subtle)',
                                                background: 'var(--button-disabled-bg)',
                                            },
                                        }}
                                    >
                                        {isSavingBotSettings ? t.saving : hasBotSettingsChanges ? t.adminSaveBotSettings : t.adminSaved}
                                    </Button>
                                </Stack>
                            </Stack>

                            {activeChatbot?.embedScript && (
                                <Stack spacing={1.2}>
                                    <Typography variant='body2' sx={{ color: 'var(--admin-section-title)', fontWeight: 700 }}>
                                        {t.adminClientIntegration}
                                    </Typography>

                                    <Alert severity='info' sx={{ borderRadius: '16px' }}>
                                        {t.adminClientIntegrationInfo}
                                    </Alert>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <TextField
                                            value={activeChatbot.embedScript}
                                            fullWidth
                                            size='small'
                                            label={t.adminWidgetScriptSnippet}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <Tooltip title={copiedField === 'embed' ? t.adminCopyTooltipCopied : t.adminCopyWidgetScript}>
                                            <span>
                                                <IconButton
                                                    onClick={() => copyToClipboard(activeChatbot.embedScript, 'embed')}
                                                    sx={{
                                                        borderRadius: '14px',
                                                        border: '1px solid var(--admin-copy-button-border)',
                                                        backgroundColor: 'var(--admin-copy-button-bg)',
                                                        color: 'var(--admin-copy-button-text)',
                                                    }}
                                                >
                                                    <ContentCopy fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <TextField
                                            value={activeChatbot.iframeSnippet || ''}
                                            fullWidth
                                            size='small'
                                            label={t.adminIframeSnippet}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <Tooltip title={copiedField === 'iframe' ? t.adminCopyTooltipCopied : t.adminCopyIframeSnippet}>
                                            <span>
                                                <IconButton
                                                    onClick={() => copyToClipboard(activeChatbot.iframeSnippet || '', 'iframe')}
                                                    sx={{
                                                        borderRadius: '14px',
                                                        border: '1px solid var(--admin-copy-button-border)',
                                                        backgroundColor: 'var(--admin-copy-button-bg)',
                                                        color: 'var(--admin-copy-button-text)',
                                                    }}
                                                >
                                                    <ContentCopy fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <TextField
                                            value={activeChatbot.directLink || ''}
                                            fullWidth
                                            size='small'
                                            label={t.adminHostedDirectLink}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <Tooltip title={copiedField === 'direct-link' ? t.adminCopyTooltipCopied : t.adminCopyDirectLink}>
                                            <span>
                                                <IconButton
                                                    onClick={() => copyToClipboard(activeChatbot.directLink || '', 'direct-link')}
                                                    sx={{
                                                        borderRadius: '14px',
                                                        border: '1px solid var(--admin-copy-button-border)',
                                                        backgroundColor: 'var(--admin-copy-button-bg)',
                                                        color: 'var(--admin-copy-button-text)',
                                                    }}
                                                >
                                                    <ContentCopy fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>

                                    <Typography variant='caption' sx={{ color: 'var(--admin-section-subtle)' }}>
                                        {t.adminAllowedDomainsEnforced}
                                    </Typography>
                                </Stack>
                            )}

                            {botError && (
                                <Alert severity='error' sx={{ borderRadius: '16px' }}>
                                    {botError}
                                </Alert>
                            )}

                            {!botError && hasBotSettingsChanges && activeChatbot && (
                                <Alert severity='info' sx={{ borderRadius: '16px' }}>
                                    {t.adminUnsavedSettingsAlert}
                                </Alert>
                            )}

                            {botSuccess && (
                                <Alert severity='success' sx={{ borderRadius: '16px' }}>
                                    {botSuccess}
                                </Alert>
                            )}
                        </Stack>
                    </Box>

                    <ChatbotCustomizationPanel key={`customization-${activePanelKey}`} tenantId={tenantId || null} chatbotId={activeBotId || null} />

                    <Box
                        key={`prompt-${activePanelKey}`}
                        sx={{
                            width: '100%',
                            '& > *': {
                                width: '100%',
                            },
                            '& > .MuiPaper-root': {
                                width: '100%',
                                maxWidth: 'none !important',
                            },
                            '& > * .MuiPaper-root:first-of-type': {
                                width: '100%',
                                maxWidth: 'none !important',
                            },
                            '& textarea': {
                                width: '100%',
                            },
                        }}
                    >
                        <PromptUpdateField />
                    </Box>

                    <Box key={`knowledge-${activePanelKey}`}>
                        <KnowledgeBasePanel />
                    </Box>

                    <Box key={`drawio-${activePanelKey}`}>
                        <DrawioUploadPanel />
                    </Box>

                    <Box key={`zipinfo-${activePanelKey}`}>
                        <ZipInfoDialogContent />
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}